import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from './shared/hooks/useAuth';
import { useImpersonation } from './shared/context/useImpersonation';
import { settingsApi } from './modules/settings/api/settingsApi';
import { settingsKeys } from './modules/settings/queryKeys';
import { authApi } from './modules/auth/api/authApi';
import { authKeys } from './modules/auth/queryKeys';
import { AUTH_CONFIG } from './config/auth';
import styles from './routes.module.css';

// Layouts
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { AgencyLayout } from './layouts/AgencyLayout';

// Auth pages
import { LoginPage, VerifyPage } from './modules/auth/pages';

// Onboarding
import { OnboardingPage } from './modules/onboarding/pages';

// Main app pages
import { OverviewPage as DashboardOverviewPage, ActionQueuePage } from './modules/dashboard/pages';
import { MissionPage, TasksPage } from './modules/agents/pages';
import {
  SEODashboardPage,
  SiteHealthPage,
  ContentPage,
  OverviewPage as SeoOverviewPage,
  PerformancePage,
} from './modules/seo/pages';
import { ReviewsPage } from './modules/reviews/pages';
import { CommunityPage } from './modules/community';
import {
  AdsDashboardPage,
  AdsRecommendationsPage,
  CampaignReviewPage,
  PerformancePage as AdsPerformancePage,
  CampaignsListPage,
  AdsActivityPage,
} from './modules/campaigns/pages';
import {
  AssetsPage,
  StrategyOverviewPage,
  PracticeDataStep,
  PracticeProfileStep,
  MarketIntelligenceStep,
  CompetitionStep,
  InsightsStep,
  ChannelMixPage,
  BrandKitPage,
  BrandPositioningPage,
  ConfirmCallStep,
} from './modules/strategy/pages';
import { MailersPage, MailerBuilderPage, LetterheadPage } from './modules/print-mail/pages';
import { CallLogPage, AgentsPage, AgentDetailPage, SourcesPage } from './modules/calls/pages';
import {
  SafetyOverviewPage,
  SafetySetupPage,
  StaffTrainingPage,
  TakeCoursePage,
  EquipmentLogsPage,
  SafetyRecordsPage,
  SafetyBinderPage,
} from './modules/safety/pages';
import {
  PracticeCredentialsPage,
  PayersPage,
  AgencyInsurancePayersPage,
  AgencyPayerDetailPage,
} from './modules/insurance/pages';
import {
  WorklistPage as IvWorklistPage,
  VerificationDetailPage as IvVerificationDetailPage,
  ImportPage as IvImportPage,
  TemplatesPage as IvTemplatesPage,
} from './modules/insuranceVerification/pages';
import {
  ReferralsPage,
  NewReferralPage,
  ReferralSettingsPage,
  PublicReferralPage,
  PatientReferralPage,
  PrintReferralPage,
} from './modules/referrals/pages';
import { useFeatureFlagState } from './shared/hooks/useFeatureFlag';
import type { FeatureFlag } from './config/features';
import {
  StrategyWizardLayout,
  StrategyBuilderIndexRedirect,
} from './modules/strategy/components/StrategyWizardLayout';
import {
  SettingsPage,
  PracticeSettingsPage,
  TeamAndStaffPage,
  PaidSettingsPage,
  OrganicSettingsPage,
  OperationsSettingsPage,
  CommunicationSettingsPage,
  NotificationsPage,
  GoogleAdsSetupPage,
  GBPSetupPage,
  GSCSetupPage,
} from './modules/settings/pages';

// Agency pages
import {
  AgencyPracticesPage,
  AgencyAgentsPage,
  PracticeWorkspacePage,
  LocationOnboardingPage,
  AgentRunLogPage,
  AgencySkillsRegistryPage,
  AgencyFlowMapPage,
  AgencySkillOverridesPage,
} from './modules/agency/pages';
import { AgencyAuditLogPage, usePageViewTracker } from './modules/audit';

// Invite
import { AcceptInvitePage } from './modules/invite/pages';

// Public patient-facing capture wizard
import { ReviewCapturePage } from './modules/reviews/pages/ReviewCapturePage';
import { GoogleHandoffPage } from './modules/reviews/pages/GoogleHandoffPage';
import { ReviewStartPage } from './modules/reviews/pages/ReviewStartPage';

// Mockups (dev only — lazy-loaded, excluded from production bundle)
const StrategyV2Mockup = React.lazy(() => import('./mockups/StrategyV2Mockup'));
const OnlineVisibilityMockup = React.lazy(() => import('./mockups/OnlineVisibilityMockup'));
const OnlineVisibilityV7Mockup = React.lazy(() => import('./mockups/OnlineVisibilityV7Mockup'));
const AdCampaignsV5Mockup = React.lazy(() => import('./mockups/AdCampaignsV5Mockup'));
const AdCampaignsV6Mockup = React.lazy(() => import('./mockups/AdCampaignsV6Mockup'));
const AdCampaignsV9Mockup = React.lazy(() => import('./mockups/AdCampaignsV9Mockup'));
const DashboardOverviewV3Mockup = React.lazy(() => import('./mockups/DashboardOverviewV3Mockup'));
const AgencyViewV2Mockup = React.lazy(() => import('./mockups/AgencyViewV2Mockup'));
const CommandCenterV1Mockup = React.lazy(() => import('./mockups/CommandCenterV1Mockup'));
const SupervisionV1Mockup = React.lazy(() => import('./mockups/SupervisionV1Mockup'));

/** The old Integrations page: keeps a connect flow's ?connected= or ?error= on the way to where it moved. */
function IntegrationsMoved() {
  const { search } = useRouterLocation();
  const params = new URLSearchParams(search);
  const outcome = params.get('connected') ?? params.get('error') ?? '';
  const target = /google_ads|meta_ads/.test(outcome) ? '/settings/paid' : '/settings/organic';
  return <Navigate to={`${target}${search}`} replace />;
}
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) return <div className={styles.loading}>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children}</>;
}

function PracticeRoute({ children }: { children: React.ReactNode }) {
  const { user, login, logout } = useAuth();
  const { impersonatedPractice } = useImpersonation();
  const [switching, setSwitching] = useState(false);

  // For regular users, check if their current practice is active
  const { data: practiceData, isLoading: practiceLoading } = useQuery({
    queryKey: settingsKeys.practice(),
    queryFn: () => settingsApi.getPractice(),
    enabled: !!user?.practiceId && !user?.isAgency,
    staleTime: 5 * 60 * 1000,
  });

  const isInactive = !user?.isAgency && practiceData?.data?.status === 'inactive';

  // Fetch other active practices when current one is inactive
  const { data: activePractices, isLoading: practicesLoading } = useQuery({
    queryKey: authKeys.practices(),
    queryFn: async () => {
      const res = await authApi.getUserPractices();
      return res.data;
    },
    enabled: isInactive,
  });

  // Auto-switch to an active practice if one exists
  useEffect(() => {
    if (!isInactive || practicesLoading || switching) return;
    if (activePractices && activePractices.length > 0) {
      const target = activePractices[0];
      const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
      if (!refreshToken) return;

      setSwitching(true);
      authApi
        .switchPractice(target.id, refreshToken)
        .then((response) => {
          const { access_token, refresh_token, user: userData } = response.data;
          login(access_token, refresh_token, userData);
          window.location.reload();
        })
        .catch(() => setSwitching(false));
    }
  }, [isInactive, activePractices, practicesLoading]);

  // Agency users must be impersonating a practice to reach the practice
  // dashboard. This guard sits below every hook deliberately: above them, the
  // component rendered zero hooks on the agency path and three on the other, so
  // React threw "rendered fewer hooks than expected" the moment the condition
  // flipped — which is what starting impersonation while on a practice route
  // does. The queries above are already gated by their own `enabled` options,
  // so running them in this order fetches nothing extra.
  if (user?.isAgency && !impersonatedPractice) {
    return <Navigate to="/agency" replace />;
  }

  if (!user?.isAgency && (practiceLoading || switching)) {
    return <div className={styles.loading}>Loading...</div>;
  }

  // No active practices at all — show message
  if (isInactive && !practicesLoading && (!activePractices || activePractices.length === 0)) {
    return (
      <div className={styles.inactivePage}>
        <h1 className={styles.inactiveTitle}>No Active Practices</h1>
        <p className={styles.inactiveMessage}>
          No active practices found for your profile. Please contact your agency administrator.
        </p>
        <button className={styles.signOutBtn} onClick={() => logout()}>
          Sign Out
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

function AgencyRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) return <div className={styles.loading}>Loading...</div>;
  if (!user?.isAgency) return <Navigate to="/" replace />;

  return <>{children}</>;
}

function FeatureRoute({ flag, children }: { flag: FeatureFlag; children: React.ReactNode }) {
  const { enabled, isLoading } = useFeatureFlagState(flag);
  // A practice-stored flag is unknown until fetched — don't bounce the user
  // to the dashboard while it loads.
  if (isLoading) return null;
  if (!enabled) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function AppRoutes() {
  // Records every route change to the access audit log. Mounted here because
  // it needs to sit inside the router and above every page.
  usePageViewTracker();

  return (
    <Routes>
      {/* Auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify" element={<VerifyPage />} />
      </Route>

      {/* Onboarding */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />

      {/* Main app routes */}
      <Route
        element={
          <ProtectedRoute>
            <PracticeRoute>
              <AppLayout />
            </PracticeRoute>
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardOverviewPage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="tasks/mission" element={<MissionPage />} />
        <Route path="actions" element={<ActionQueuePage />} />

        {/* Online Visibility */}
        <Route path="seo">
          <Route index element={<SeoOverviewPage />} />
          <Route path="content" element={<ContentPage />} />
          <Route path="performance" element={<PerformancePage />} />
          <Route path="ai-visibility" element={<Navigate to="/seo/performance" replace />} />
          <Route path="site-health" element={<SiteHealthPage />} />
          <Route path="reviews" element={<ReviewsPage />} />
          <Route path="audit" element={<SEODashboardPage />} />
        </Route>

        {/* Ads */}
        <Route path="campaigns">
          <Route index element={<AdsDashboardPage />} />
          <Route path="list" element={<CampaignsListPage />} />
          <Route path="performance" element={<AdsPerformancePage />} />
          <Route path="activity" element={<AdsActivityPage />} />
          {/* Legacy: /campaigns/recommendations is now folded into /campaigns/list */}
          <Route path="recommendations" element={<AdsRecommendationsPage />} />
          <Route path=":campaignId" element={<CampaignReviewPage />} />
        </Route>

        {/* Strategy */}
        <Route path="strategy">
          <Route index element={<StrategyOverviewPage />} />
          <Route path="builder" element={<StrategyWizardLayout />}>
            <Route index element={<StrategyBuilderIndexRedirect />} />
            <Route path="profile" element={<PracticeProfileStep />} />
            <Route path="brand/confirm" element={<ConfirmCallStep />} />
            <Route path="data" element={<PracticeDataStep />} />
            <Route path="market" element={<MarketIntelligenceStep />} />
            <Route path="competition" element={<CompetitionStep />} />
            <Route path="insights" element={<InsightsStep />} />
          </Route>
          <Route path="brand-positioning" element={<BrandPositioningPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route
            path="channel-mix"
            element={
              <FeatureRoute flag="channel-mix">
                <ChannelMixPage />
              </FeatureRoute>
            }
          />
          <Route
            path="brand-kit"
            element={
              <FeatureRoute flag="brand-kit">
                <BrandKitPage />
              </FeatureRoute>
            }
          />
        </Route>

        {/* Print & Mail */}
        <Route path="print-mail">
          <Route
            path="mailers"
            element={
              <FeatureRoute flag="print-mail">
                <MailersPage />
              </FeatureRoute>
            }
          />
          <Route
            path="mailers/:mailerId"
            element={
              <FeatureRoute flag="print-mail">
                <MailerBuilderPage />
              </FeatureRoute>
            }
          />
          <Route
            path="letterhead"
            element={
              <FeatureRoute flag="print-mail">
                <LetterheadPage />
              </FeatureRoute>
            }
          />
        </Route>

        {/* Community & Referrals (Joe): behind the `community` practice flag; the API 404s without it. */}
        <Route
          path="community"
          element={
            <FeatureRoute flag="community">
              <CommunityPage />
            </FeatureRoute>
          }
        />

        {/* Patient Referrals (Operations): behind the `patientReferrals` practice flag; the API 404s without it. */}
        <Route path="referrals">
          <Route
            index
            element={
              <FeatureRoute flag="patient-referrals">
                <ReferralsPage />
              </FeatureRoute>
            }
          />
          <Route
            path="new"
            element={
              <FeatureRoute flag="patient-referrals">
                <NewReferralPage />
              </FeatureRoute>
            }
          />
          <Route
            path="settings"
            element={
              <FeatureRoute flag="patient-referrals">
                <ReferralSettingsPage />
              </FeatureRoute>
            }
          />
          <Route
            path=":id"
            element={
              <FeatureRoute flag="patient-referrals">
                <ReferralsPage />
              </FeatureRoute>
            }
          />
          <Route
            path=":id/edit"
            element={
              <FeatureRoute flag="patient-referrals">
                <NewReferralPage />
              </FeatureRoute>
            }
          />
        </Route>

        {/* Calls */}
        <Route path="calls">
          <Route
            path="log"
            element={
              <FeatureRoute flag="calls">
                <CallLogPage />
              </FeatureRoute>
            }
          />
          <Route path="agents">
            <Route
              index
              element={
                <FeatureRoute flag="calls">
                  <AgentsPage />
                </FeatureRoute>
              }
            />
            <Route
              path=":agentId"
              element={
                <FeatureRoute flag="calls">
                  <AgentDetailPage />
                </FeatureRoute>
              }
            />
          </Route>
          <Route path="numbers" element={<Navigate to="/settings/operations" replace />} />
          <Route
            path="sources"
            element={
              <FeatureRoute flag="calls">
                <SourcesPage />
              </FeatureRoute>
            }
          />
        </Route>

        {/* Safety & Training (Operations) */}
        <Route path="safety">
          <Route
            index
            element={
              <FeatureRoute flag="safety-training">
                <SafetyOverviewPage />
              </FeatureRoute>
            }
          />
          <Route
            path="setup"
            element={
              <FeatureRoute flag="safety-training">
                <SafetySetupPage />
              </FeatureRoute>
            }
          />
          <Route path="training">
            <Route
              index
              element={
                <FeatureRoute flag="safety-training">
                  <StaffTrainingPage />
                </FeatureRoute>
              }
            />
            <Route
              path=":assignmentId/take"
              element={
                <FeatureRoute flag="safety-training">
                  <TakeCoursePage />
                </FeatureRoute>
              }
            />
          </Route>
          <Route
            path="equipment"
            element={
              <FeatureRoute flag="safety-training">
                <EquipmentLogsPage />
              </FeatureRoute>
            }
          />
          <Route
            path="records"
            element={
              <FeatureRoute flag="safety-training">
                <SafetyRecordsPage />
              </FeatureRoute>
            }
          />
        </Route>

        {/* Insurance Verification (Operations) */}
        <Route path="insurance">
          <Route
            path="credentials"
            element={
              <FeatureRoute flag="insurance-verification">
                <FeatureRoute flag="insurance-verification-legacy">
                  <PracticeCredentialsPage />
                </FeatureRoute>
              </FeatureRoute>
            }
          />
          <Route
            path="payers"
            element={
              <FeatureRoute flag="insurance-verification">
                <FeatureRoute flag="insurance-verification-legacy">
                  <PayersPage />
                </FeatureRoute>
              </FeatureRoute>
            }
          />
        </Route>

        {/* Insurance Verification — worklist, import, templates, connections */}
        <Route path="insurance-verification">
          <Route
            index
            element={
              <FeatureRoute flag="insurance-verification">
                <IvWorklistPage />
              </FeatureRoute>
            }
          />
          <Route
            path="verifications/:id"
            element={
              <FeatureRoute flag="insurance-verification">
                <IvVerificationDetailPage />
              </FeatureRoute>
            }
          />
          <Route
            path="import"
            element={
              <FeatureRoute flag="insurance-verification">
                <IvImportPage />
              </FeatureRoute>
            }
          />
          <Route
            path="templates"
            element={
              <FeatureRoute flag="insurance-verification">
                <IvTemplatesPage />
              </FeatureRoute>
            }
          />
          <Route path="connections" element={<Navigate to="/settings/operations" replace />} />
          <Route path="credentials" element={<Navigate to="/settings/operations" replace />} />
        </Route>

        {/* Settings */}
        <Route path="settings">
          <Route index element={<SettingsPage />} />
          <Route path="practice" element={<PracticeSettingsPage />} />
          <Route path="locations" element={<Navigate to="/settings/practice" replace />} />
          <Route path="team" element={<TeamAndStaffPage />} />
          <Route path="staff" element={<Navigate to="/settings/team" replace />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="paid" element={<PaidSettingsPage />} />
          <Route path="organic" element={<OrganicSettingsPage />} />
          <Route path="operations" element={<OperationsSettingsPage />} />
          <Route path="communication" element={<CommunicationSettingsPage />} />
          <Route path="texting" element={<Navigate to="/settings/communication" replace />} />
          {/* Integrations moved: the ad accounts to Paid, the search connections to Organic & Offline. */}
          <Route path="integrations" element={<IntegrationsMoved />} />
          <Route path="integrations/google-ads/setup" element={<GoogleAdsSetupPage />} />
          <Route path="integrations/google-business-profile/setup" element={<GBPSetupPage />} />
          <Route path="integrations/google-search-console/setup" element={<GSCSetupPage />} />
        </Route>
      </Route>

      {/* Inspection binder — printable, rendered without the app chrome */}
      <Route
        path="/safety/records/binder"
        element={
          <ProtectedRoute>
            <FeatureRoute flag="safety-training">
              <SafetyBinderPage />
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      {/* Patient Referrals — printable office and patient copies, rendered without the app chrome */}
      <Route
        path="/referrals/:id/print"
        element={
          <ProtectedRoute>
            <FeatureRoute flag="patient-referrals">
              <PrintReferralPage />
            </FeatureRoute>
          </ProtectedRoute>
        }
      />

      {/* Agency routes */}
      <Route
        path="/agency"
        element={
          <AgencyRoute>
            <AgencyLayout />
          </AgencyRoute>
        }
      >
        {/*
          Four places. Practices is home — one row per practice, with the
          cross-practice queue as a view of the same page (?view=queue). The
          practice workspace holds everything about one practice, one location
          at a time. Agents holds what an agent is for every practice (skills,
          flow map, overrides). The old Today, Portfolio, Rollout & Flags,
          Skills Registry, Flow Map, Overrides and Settings routes redirect to
          where their functions now live.
        */}
        <Route index element={<AgencyPracticesPage />} />
        <Route path="today" element={<Navigate to="/agency?view=queue" replace />} />
        <Route path="portfolio" element={<Navigate to="/agency" replace />} />
        <Route path="practices" element={<Navigate to="/agency" replace />} />
        <Route path="practices/:id" element={<PracticeWorkspacePage />} />
        <Route path="insurance-payers">
          <Route index element={<AgencyInsurancePayersPage />} />
          <Route path=":payerId" element={<AgencyPayerDetailPage />} />
        </Route>
        <Route
          path="practices/:practiceId/locations/:locationId/onboarding"
          element={<LocationOnboardingPage />}
        />
        <Route
          path="practices/:practiceId/locations/:locationId/agents/:roleKey/runs"
          element={<AgentRunLogPage />}
        />
        <Route path="audit-log" element={<AgencyAuditLogPage />} />
        <Route path="agents" element={<AgencyAgentsPage />}>
          <Route index element={<Navigate to="skills" replace />} />
          <Route path="skills" element={<AgencySkillsRegistryPage />} />
          <Route path="flow" element={<AgencyFlowMapPage />} />
          <Route path="overrides" element={<AgencySkillOverridesPage />} />
        </Route>
        {/* Per-practice agent switches moved onto each practice's Agents tab. */}
        <Route path="rollout" element={<Navigate to="/agency" replace />} />
        <Route path="skills" element={<Navigate to="/agency/agents/skills" replace />} />
        <Route path="skills/flow" element={<Navigate to="/agency/agents/flow" replace />} />
        <Route
          path="skills/overrides"
          element={<Navigate to="/agency/agents/overrides" replace />}
        />
        {/* The Meta Ads token lives with the Ads module of any practice now. */}
        <Route path="settings/integrations" element={<Navigate to="/agency" replace />} />
      </Route>

      {/* Invitation acceptance — public, no auth */}
      <Route path="/invite/:token" element={<AcceptInvitePage />} />

      {/* Public review capture — no auth, patient-facing */}
      <Route path="/review/start/:locationRef" element={<ReviewStartPage />} />
      <Route path="/r/:shortCode" element={<ReviewCapturePage />} />
      {/* Patient Referrals: the receiving office's emailed link and the patient's text link. No account. */}
      <Route path="/refer/v/:token" element={<PublicReferralPage />} />
      <Route path="/refer/p/:token" element={<PatientReferralPage />} />
      {/* Betty's text hand-off: straight to the practice's Google review box */}
      <Route path="/g/:shortCode" element={<GoogleHandoffPage />} />

      {/* Mockups (dev only — tree-shaken out of production builds) */}
      {import.meta.env.DEV && (
        <>
          <Route
            path="/mockup/strategy-v2"
            element={
              <React.Suspense>
                <StrategyV2Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/online-visibility"
            element={
              <React.Suspense>
                <OnlineVisibilityMockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/online-visibility-v7"
            element={
              <React.Suspense>
                <OnlineVisibilityV7Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/ad-campaigns-v5"
            element={
              <React.Suspense>
                <AdCampaignsV5Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/ad-campaigns-v6"
            element={
              <React.Suspense>
                <AdCampaignsV6Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/ad-campaigns-v9"
            element={
              <React.Suspense>
                <AdCampaignsV9Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/dashboard-overview-v3"
            element={
              <React.Suspense>
                <DashboardOverviewV3Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/agency-view-v2"
            element={
              <React.Suspense>
                <AgencyViewV2Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/command-center-v1"
            element={
              <React.Suspense>
                <CommandCenterV1Mockup />
              </React.Suspense>
            }
          />
          <Route
            path="/mockup/supervision-v1"
            element={
              <React.Suspense>
                <SupervisionV1Mockup />
              </React.Suspense>
            }
          />
        </>
      )}

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
